import {
  type HighlightsController,
  type HighlightsRenderer,
} from '@databases/highlights';
import { HIGHLIGHT_FOCUS_BOX_CLASS } from '@databases/highlights/constants';

import { createEffect } from '../utils/create-effect';

import { FOCUS_LAYER_Z_INDEX, SELECTIONS_FOCUS_BOX_HIGHLIGHT_ID } from './constants';
import {
  createResizeListener,
  RESIZE_OBSERVER_ELEMENT_ID,
} from './utils/create-resize-listener';

/**
 * Creates the effect that draws the focus box (the main cell border)
 * on the currently focused cell.
 *
 * Listens to `focusChanged` for direct focus movements (e.g. Tab, Enter,
 * Arrow Keys inside a selection), as well as `selectionModified` and
 * `selectionReplaced` because both carry an optional `focusChange` sub-field
 * when focus moves as part of a selection operation.
 */
export const createFocusBoxEffect = createEffect((controller: HighlightsController) => {
  // ---------------------------------------------------------------------------
  // DOM
  // ---------------------------------------------------------------------------
  let scrollableContainer: HTMLElement | null = null;

  // ---------------------------------------------------------------------------
  // Renderers
  // ---------------------------------------------------------------------------
  let focusRenderer: HighlightsRenderer | null = null;

  // Track our cleanup function
  let resizeObserverCleanup: (() => void) | null = null;

  function ensureDependentElementsAndRenderer() {
    controller.mount();

    if (!scrollableContainer) {
      scrollableContainer = controller.getScrollContainer();
    }

    if (!focusRenderer && scrollableContainer) {
      focusRenderer = controller.createLayer(FOCUS_LAYER_Z_INDEX);
      focusRenderer.add(
        {
          id: SELECTIONS_FOCUS_BOX_HIGHLIGHT_ID,
          anchor: { row: 0, col: 0 },
          lead: { row: 0, col: 0 },
        },
        'none',
        HIGHLIGHT_FOCUS_BOX_CLASS
      );
    }

    if (!resizeObserverCleanup && scrollableContainer) {
      const resizeElement = document.getElementById(RESIZE_OBSERVER_ELEMENT_ID);

      if (resizeElement) {
        resizeObserverCleanup = createResizeListener(resizeElement, () =>
          focusRenderer?.redrawAll()
        );
      }
    }

    return !!(focusRenderer && scrollableContainer);
  }

  return {
    deltaTypes: [
      'focusChanged',
      'selectionModified',
      'selectionReplaced',
      'cellEditRequested',
      'cellEditStopped',
    ],

    dispose() {
      if (resizeObserverCleanup) {
        resizeObserverCleanup();

        resizeObserverCleanup = null;
      }

      if (focusRenderer) {
        controller.removeLayer(focusRenderer);

        focusRenderer = null;
      }

      scrollableContainer = null;
    },

    onDelta(delta, _state) {
      if (
        !ensureDependentElementsAndRenderer() ||
        !focusRenderer ||
        !scrollableContainer
      ) {
        return;
      }

      if (delta.kind === 'cellEditRequested') {
        focusRenderer.hideAll();

        return;
      }

      if (delta.kind === 'cellEditStopped') {
        focusRenderer.showAll();

        return;
      }

      const focusChange = delta.kind === 'focusChanged' ? delta : delta.focusChange;

      if (focusChange) {
        const row = focusChange.next?.row.index ?? 0;
        const col = focusChange.next?.col.index ?? 0;

        focusRenderer.update(
          {
            id: SELECTIONS_FOCUS_BOX_HIGHLIGHT_ID,
            anchor: { row, col },
            lead: { row, col },
          },
          focusChange.next ? 'cell' : 'none'
        );
      }
    },
  };
});
